// olap-client/src/utils/pushNotifications.js
import axios from 'axios';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
const API_URL = process.env.REACT_APP_API_URL || 'https://olap.ak-i.ru';

console.log('🔑 [Push] VAPID_PUBLIC_KEY:', VAPID_PUBLIC_KEY ? 'loaded (' + VAPID_PUBLIC_KEY.substring(0, 20) + '...)' : 'MISSING!');
console.log('🌐 [Push] API_URL:', API_URL);

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerServiceWorker() {
  console.log('📝 [Push] registerServiceWorker called');
  
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ [Push] Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('✅ [Push] Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('❌ [Push] Service Worker registration failed:', error);
    return null;
  }
}

export async function requestNotificationPermission() {
  console.log('🔔 [Push] requestNotificationPermission called');
  
  if (!('Notification' in window)) {
    console.warn('⚠️ [Push] Notifications not supported');
    return false;
  }

  console.log('🔔 [Push] Current permission:', Notification.permission);

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    console.warn('⚠️ [Push] Notifications denied by user');
    return false;
  }

  const permission = await Notification.requestPermission();
  console.log('🔔 [Push] Permission result:', permission);
  return permission === 'granted';
}

export async function getExistingSubscription() {
  console.log('🔍 [Push] getExistingSubscription called');
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      console.log('📭 [Push] No existing subscription found');
      return null;
    }
    
    console.log('✅ [Push] Existing subscription found:', subscription.endpoint.substring(0, 50) + '...');
    return subscription;
  } catch (error) {
    console.error('❌ [Push] Error checking subscription:', error);
    return null;
  }
}

export async function createNewSubscription() {
  console.log('📝 [Push] createNewSubscription called');
  console.log('🔑 [Push] Using VAPID key:', VAPID_PUBLIC_KEY ? 'YES' : 'NO');
  
  if (!VAPID_PUBLIC_KEY) {
    console.error('❌ [Push] VAPID_PUBLIC_KEY is missing! Check .env file');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
    
    console.log('✅ [Push] New subscription created');
    return subscription;
  } catch (error) {
    console.error('❌ [Push] Failed to create subscription:', error);
    return null;
  }
}

export async function sendSubscriptionToServer(subscription) {
  console.log('📤 [Push] sendSubscriptionToServer called');
  
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ [Push] No token, cannot send subscription');
      return false;
    }
    
    console.log('📤 [Push] Sending to:', `${API_URL}/api/chat/push/subscribe`);
    
    await axios.post(
      `${API_URL}/api/chat/push/subscribe`,
      subscription.toJSON(),
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    
    console.log('✅ [Push] Subscription sent to server');
    return true;
  } catch (error) {
    console.error('❌ [Push] Failed to send subscription to server:', error);
    console.error('❌ [Push] Error details:', error.response?.data || error.message);
    return false;
  }
}

export async function ensurePushSubscription() {
  console.log('🔍 [Push] === ensurePushSubscription START ===');
  
  try {
    // 1. Проверяем поддержку
    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      console.log('⚠️ [Push] Push notifications not supported');
      return false;
    }
    
    // 2. Проверяем разрешение
    console.log('🔍 [Push] Current permission:', Notification.permission);
    if (Notification.permission === 'denied') {
      console.log('⚠️ [Push] Notifications denied by user');
      return false;
    }
    
    // 3. Запрашиваем разрешение, если нужно
    if (Notification.permission === 'default') {
      const granted = await requestNotificationPermission();
      if (!granted) {
        console.log('⚠️ [Push] Permission not granted');
        return false;
      }
    }
    
    // 4. Регистрируем Service Worker
    const registration = await registerServiceWorker();
    if (!registration) {
      console.log('⚠️ [Push] Service Worker not registered');
      return false;
    }
    
    // 5. Проверяем существующую подписку
    let subscription = await getExistingSubscription();
    
    if (!subscription) {
      // 6. Создаём новую подписку
      console.log('📝 [Push] Creating new subscription...');
      subscription = await createNewSubscription();
      
      if (!subscription) {
        console.log('❌ [Push] Failed to create subscription');
        return false;
      }
      
      // 7. Отправляем на сервер
      await sendSubscriptionToServer(subscription);
      console.log('✅ [Push] === Push subscription FULLY SET UP ===');
      return true;
    } else {
      console.log('✅ [Push] Subscription already exists, verifying...');
      await sendSubscriptionToServer(subscription);
      console.log('✅ [Push] === Subscription verified and synced ===');
      return true;
    }
  } catch (error) {
    console.error('❌ [Push] ensurePushSubscription error:', error);
    console.error('❌ [Push] Error stack:', error.stack);
    return false;
  }
}

export async function subscribeToPush() {
  return ensurePushSubscription();
}

export async function sendTestPush() {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/api/chat/push/test`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    console.log('✅ [Push] Test push sent:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [Push] Test push failed:', error);
    return null;
  }
}

// === НОВАЯ ФУНКЦИЯ: ОТПИСКА ПРИ LOGOUT ===
export async function unsubscribeFromPush() {
  console.log('🗑️ [Push] Unsubscribing from push notifications...');
  
  try {
    const token = localStorage.getItem('token');
    
    // 1. Получаем текущую подписку
    let subscription = null;
    try {
      subscription = await getExistingSubscription();
    } catch (e) {
      console.log('⚠️ [Push] Could not get subscription:', e.message);
    }
    
    // 2. Отписываемся на сервере
    if (token) {
      try {
        await axios.post(
          `${API_URL}/api/chat/push/unsubscribe`,
          subscription ? { endpoint: subscription.endpoint } : {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ [Push] Unsubscribed on server');
      } catch (error) {
        console.error('❌ [Push] Failed to unsubscribe on server:', error.message);
      }
    }
    
    // 3. Отписываемся в браузере
    if (subscription) {
      try {
        await subscription.unsubscribe();
        console.log('✅ [Push] Unsubscribed in browser');
      } catch (e) {
        console.error('❌ [Push] Failed to unsubscribe in browser:', e.message);
      }
    }
    
    console.log('✅ [Push] Push subscription fully removed');
    return true;
  } catch (error) {
    console.error('❌ [Push] unsubscribeFromPush error:', error);
    return false;
  }
}

export function getNotificationStatus() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function shouldRequestPermission() {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'default';
}